import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Clock,
  BookOpen,
  PlayCircle,
  Award,
  User,
  Calendar,
  Video,
  Search,
  SortAsc,
  SortDesc,
  AlertCircle,
  CalendarX,
  X,
  Download,
  MessageCircle,
  Layers,
  CheckCircle2,
  Percent,
  Repeat,
  ChevronDown,
  ChevronRight,
  MapPin,
  Briefcase,
  FileText,
  GraduationCap,
  UserCheck,
  TrendingUp,
  Play,
  Phone,
  Mail,
  ExternalLink,
  ContactRound,
  ClipboardList,
  Trophy,
  MessageSquareHeart,
} from 'lucide-react';
import moment from 'moment';
import { Skeleton, SkeletonGroup } from '../../common/Skeleton/Skeleton.jsx';
import { silenceFor } from '../../common/Loader/busy';
import { useIsMobile } from '../../../../utils/useIsMobile';
import { toast } from '../../common/Toast/toast';
import debounce from 'lodash/debounce';

import {
  getStudentDashboardBatches,
  getStudentDashboardAttendance,
  getStudentDashboardCourses,
  getStudentDashboardSuggested,
  getStudentDashboardFeed,
} from '../../../../services/StudentDashboardService';
import { getStudentOptions } from '../../../../services/StudentService';
import { getBlogs } from '../../../../services/BlogService.js';
import CustomAsyncDropdown from '../../common/NewCustomAsyncSelectDropdown.jsx';
import BatchChangeRequestsCard from './BatchChangeRequest/BatchChangeRequestsCard.jsx';
import { CONTACTS } from './ContactInfo.jsx';
import { useAuth } from '../../../../context/AuthContext';

/* The staff view is the admin and trainer dashboards' own kit, so it
   pulls their two stylesheets rather than restating them. Everything
   this page adds — the picker, the gate, and the whole LinkedIn view —
   lives in StudentDashboard.css. */
import { useWaypoint } from '../../common/waypoint/WaypointProvider.jsx';
import '../../Trainer/TrainerDashboard.css';
import '../../Dashboard/BusinessPulse.css';
import '../../common/waypoint/waypoint.css';
import './StudentDashboard.css';

/* =============================================================
   WHO IS LOOKING

   Two dashboards live behind /student-dashboard. A student gets the
   LinkedIn-shaped view of their own record; anybody else gets the
   staff view, which reads like /dashboard and /trainer-dashboard
   because the people using it spend their day on those two.

   A profile that is named and is not a student profile is staff. Both
   uncertain cases fall to the student view on purpose: the staff view
   carries a picker that reaches every other student on the system, so
   a viewer we cannot place must not be handed one. Being wrong the
   other way only shows somebody their own record.

   The student test is "contains student" rather than "equals student"
   for the same reason — a label like "Student (Alumni)" must not read
   as staff. Staff labels elsewhere in the app are matched the same
   loose way; AdminDashboard.jsx:120 tests for 'crm' with includes.
   ============================================================= */
export const isStaffViewer = (userData) => {
  if (!userData) return false;

  if (
    userData.isSuperAdmin === true ||
    userData.isSuperAdmin === 'true' ||
    userData.isSuperAdmin === 1 ||
    String(userData.profileIds || '').toLowerCase() === 'admin'
  ) {
    return true;
  }

  const labels = (userData.profiles || [])
    .map((p) => String(p?.label || p?.name || '').trim().toLowerCase())
    .filter(Boolean);

  if (labels.length === 0) return false;
  if (labels.some((l) => l.includes('student'))) return false;
  return true;
};

/* =============================================================
   SHARED READERS

   Formatting only. Everything the dashboard SAYS about a batch -- its
   state (live / soon / done), how far through its window it is, and
   its course, trainer, room and mode names -- arrives already worked
   out on the batch (studentdashboard.service.js, decorateBatch), as do
   the counts, the attendance band, the course rows, the "next up"
   banner and the feed order. This file prints them and decides nothing.
   ============================================================= */
const initialsOf = (text, fallback = '?') => {
  const parts = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  return parts.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
};



const STATE_WORD = { live: 'Running now', soon: 'Starts soon', done: 'Completed' };

/* The tour video shown in the right rail. Kept in step with
   FeaturedVideo.jsx, which is the same video on any page still using
   that component. Change it in both places. */
const TOUR_VIDEO_ID = 'oYvJeunU4aQ';

const dayOf = (value) => (value ? moment(value).format('DD MMM YYYY') : '—');
const timeOf = (value) => (value ? moment(value).format('hh:mm A') : '—');

/* The six facts a batch card states, as data rather than as six blocks
   of JSX. The card renders them on a desktop and the phone's bottom
   sheet renders the same list, so building it twice is how the two
   quietly stop agreeing. Room is dropped when the batch has none --
   an online batch has no classroom and an empty row is worse than no
   row. */
const factsOf = (batch) => [
  { key: 'trainer', icon: User, label: 'Trainer', value: batch.trainerName },
  {
    key: 'time',
    icon: Clock,
    label: 'Class time',
    value: `${timeOf(batch.startTime)} – ${timeOf(batch.endTime)}`,
  },
  {
    key: 'runs',
    icon: Calendar,
    label: 'Runs',
    value: `${dayOf(batch.startTime)} – ${dayOf(batch.endTime)}`,
  },
  { key: 'mode', icon: Layers, label: 'Mode', value: batch.modeLabel },
  ...(batch.classroomName
    ? [{ key: 'room', icon: MapPin, label: 'Room', value: batch.classroomName }]
    : []),
  {
    key: 'attended',
    icon: CheckCircle2,
    label: 'Attended',
    value: `${batch.attendanceCount ?? 0} class${
      (batch.attendanceCount ?? 0) === 1 ? '' : 'es'
    }`,
  },
];

/* The speedometer both other dashboards carry: a half circle of radius
   80 swept left to right, so the arc is half a circumference and not a
   whole one. Track and fill share the constant so they cannot drift. */
const GAUGE_R = 80;
const GAUGE_ARC = `M 20 100 A ${GAUGE_R} ${GAUGE_R} 0 0 1 180 100`;
const GAUGE_LEN = Math.PI * GAUGE_R;

/* =============================================================
   STAFF VIEW

   The admin and trainer kit, unchanged: .td-page for the shell,
   .bp-hero for the name box, .ov-tiles for the readouts, .td-tbl for
   every list. The only new classes are the picker and the column
   grids, both in StudentDashboard.css.
   ============================================================= */
const StaffStudentView = ({
  loading,
  dashboardData,
  enrolledCourses,
  studentName,
  studentEmail,
  studentId,
  picker,
  onClear,
  requestsRefreshSignal,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  /* The nav bridge. Everything below that names a batch can open it --
     this view does not know what a batch popup looks like and does not
     need to, it knows an id and a type. */
  const { open: openEntity } = useWaypoint();

  const batches = useMemo(
    () => (Array.isArray(dashboardData?.batches) ? dashboardData.batches : []),
    [dashboardData],
  );
  const upcoming = useMemo(
    () => (Array.isArray(dashboardData?.upcomingBatches) ? dashboardData.upcomingBatches : []),
    [dashboardData],
  );

  const running = useMemo(
    () => (Array.isArray(dashboardData?.activeBatches) ? dashboardData.activeBatches : []),
    [dashboardData],
  );

  const attendance = Number(dashboardData?.attendancePercentage) || 0;
  const attendanceTone = dashboardData?.attendanceTone ? `is-${dashboardData.attendanceTone}` : '';
  const batchCount = dashboardData?.totalBatches ?? 0;
  const activeCount = dashboardData?.activeBatchesCount ?? 0;
  const doneCount = dashboardData?.completedBatchesCount ?? 0;
  const upcomingCount = dashboardData?.upcomingBatchesCount ?? 0;
  const courseCount = dashboardData?.totalCourseEnrolled ?? 0;

  /* Attendance is the one figure staff scan for, so it is the one figure
     that carries a colour. Everything else stays near-black. */
  /* Each tile that stands for a list of things opens that list, as a tab
     on this student in the bridge. They are not separate entities -- they
     are one student read several ways, which is what the tabs are.

     "Classes attended" is the one figure with no list behind it: it is a
     total, not a collection, and the attendance tab already reports it.
     A tile that opened something showing a different number would be
     worse than one that does not open. */
  const openTab = (tab, enabled) =>
    studentId && enabled ? () => openEntity('student', studentId, { tab }) : null;

  const TILES = [
    {
      icon: BookOpen,
      label: 'Courses enrolled',
      value: dashboardData?.totalCourseEnrolled ?? 0,
      onOpen: openTab('courses', (dashboardData?.totalCourseEnrolled ?? 0) > 0),
    },
    {
      icon: Percent,
      label: 'Attendance',
      value: `${attendance}%`,
      tone: attendanceTone,
      onOpen: openTab('attendance', true),
    },
    { icon: CheckCircle2, label: 'Classes attended', value: dashboardData?.classesAttended ?? 0 },
    {
      icon: Layers,
      label: 'Batches',
      value: batchCount,
      onOpen: openTab('batches', batchCount > 0),
    },
    {
      icon: Repeat,
      label: 'Running now',
      value: activeCount,
      onOpen: openTab('running', activeCount > 0),
    },
    {
      icon: Calendar,
      label: 'Upcoming',
      value: upcomingCount,
      onOpen: openTab('upcoming', upcomingCount > 0),
    },
  ];

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'batches', label: 'Batches', count: batchCount },
    { key: 'courses', label: 'Courses', count: courseCount },
    { key: 'upcoming', label: 'Upcoming', count: upcomingCount },
    { key: 'requests', label: 'Batch requests' },
  ];

  return (
    <div className="dashboard-wrapper">
      <div className="td-page">
        {/* Who is being read, and the control that swaps them. */}
        <div className="sv-picker">
          <div className="sv-picker-id">
            <span className="sv-avatar">{initialsOf(studentName, 'S')}</span>
            <div>
              <div className="sv-picker-name">{studentName || 'Student'}</div>
              <div className="sv-picker-mail">{studentEmail || 'No email on record'}</div>
            </div>
          </div>

          <div className="sv-picker-field">
            <label className="sv-picker-label" htmlFor="sv-student-picker">Viewing student</label>
            {picker}
          </div>

          <button type="button" className="sv-clear" onClick={onClear}>
            <X size={13} />
            Clear
          </button>
        </div>

        {/* Identity over the dark ground, the figures gridded beside it —
            the same row the admin dashboard opens with. */}
        <div className="sv-top">
          <section className="bp-hero">
            <span className="bp-hero-eyebrow">Student record</span>
            <h1 className="bp-hero-title">{studentName || 'Student'}</h1>

            <div className="bp-hero-figure">
              <span className="bp-hero-label">Attendance</span>

              <div
                className="bp-gauge"
                role="img"
                aria-label={`Attendance ${attendance} percent`}
              >
                <svg viewBox="0 0 200 116" aria-hidden="true">
                  <path className="bp-gauge-track" d={GAUGE_ARC} />
                  <path
                    className="bp-gauge-fill"
                    d={GAUGE_ARC}
                    strokeDasharray={GAUGE_LEN}
                    strokeDashoffset={GAUGE_LEN * (1 - attendance / 100)}
                  />
                </svg>
                <div className="bp-gauge-face">
                  <b>{attendance}</b>
                  <small>{dashboardData?.attendanceBand || 'No data'}</small>
                </div>
                <div className="bp-gauge-scale" aria-hidden="true">
                  <span>0</span>
                  <span>100</span>
                </div>
              </div>

              <p className="bp-hero-note">
                Sessions marked present against sessions scheduled, across every batch.
              </p>
            </div>

            <dl className="bp-hero-facts">
              <div>
                <dt>Classes attended</dt>
                <dd>{dashboardData?.classesAttended ?? 0}</dd>
              </div>
              <div>
                <dt>Running now</dt>
                <dd>{activeCount}</dd>
              </div>
              <div>
                <dt>Completed</dt>
                <dd>{doneCount}</dd>
              </div>
              <div>
                <dt>Upcoming</dt>
                <dd>{upcomingCount}</dd>
              </div>
            </dl>
          </section>

          <div className="ov-tiles">
            {TILES.map(({ icon: Icon, label, value, tone, onOpen }) => {
              // A tile that goes somewhere is a button; one that does not
              // stays a div, so only the ones that act look like they do.
              const Tag = onOpen ? 'button' : 'div';
              return (
                <Tag
                  className={`ov-tile${onOpen ? ' ov-tile--open' : ''}`}
                  key={label}
                  {...(onOpen
                    ? { type: 'button', onClick: onOpen, title: `Open ${label.toLowerCase()}` }
                    : {})}
                >
                  <span className="ov-tile-label">
                    <Icon size={13} aria-hidden="true" />
                    {label}
                  </span>
                  <span className={`ov-tile-value${tone ? ` sv-att ${tone}` : ''}`}>
                    {loading ? <Skeleton shape="title" width="40" /> : value}
                  </span>
                </Tag>
              );
            })}
          </div>
        </div>

        <div className="td-tabs">
          <div className="td-tabs-strip" role="tablist">
            {TABS.map((tab) => (
              <button
                type="button"
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`td-tab${activeTab === tab.key ? ' is-on' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tab.count !== undefined && <span className="td-tab-n">{tab.count}</span>}
              </button>
            ))}
          </div>

          <div className="td-tabs-body">
            {activeTab === 'overview' && (
              <div className="ov">
                <div className="ov-duo">
                  {/* Batches this student is in right now */}
                  <section className="ov-panel">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">
                        Current batches
                        <span className="ov-panel-count">{activeCount}</span>
                      </h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => setActiveTab('batches')}
                      >
                        All batches
                      </button>
                    </header>

                    {running.length === 0 ? (
                      <p className="ov-empty">Nothing in session.</p>
                    ) : (
                      <ul className="ov-list">
                        {running
                          .slice(0, 5)
                          .map((batch) => (
                            <li key={batch.id || batch._id}>
                              {/* --static dropped: .ov-row was always built
                                  as a button (cursor, hover, focus ring) and
                                  --static is its opt-out for when there is
                                  nowhere to go. There is now. */}
                              <button
                                type="button"
                                className="ov-row"
                                onClick={() => openEntity('batch', batch.id || batch._id)}
                              >
                                <span className="ov-row-mark">{initialsOf(batch.name, 'B')}</span>
                                <span className="ov-row-main">
                                  <span className="ov-row-name">{batch.name}</span>
                                  <span className="ov-row-sub">
                                    {batch.courseName} · {batch.trainerName}
                                  </span>
                                </span>
                                <span className="ov-row-side">{batch.modeLabel}</span>
                              </button>
                            </li>
                          ))}
                      </ul>
                    )}
                  </section>

                  {/* Course progress, as a bar per course */}
                  <section className="ov-panel">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">
                        Course progress
                        <span className="ov-panel-count">{courseCount}</span>
                      </h2>
                      <button
                        type="button"
                        className="ov-panel-link"
                        onClick={() => setActiveTab('courses')}
                      >
                        All courses
                      </button>
                    </header>

                    {enrolledCourses.length === 0 ? (
                      <p className="ov-empty">Not enrolled in any course.</p>
                    ) : (
                      <ul className="ov-list">
                        {enrolledCourses.slice(0, 5).map((course, i) => {
                          const pct = course.progress;
                          return (
                            <li key={`${course.courseName}-${i}`}>
                              <div className="ov-row ov-row--static">
                                <span className="ov-row-main">
                                  <span className="ov-row-name">{course.courseName}</span>
                                  <span className="ov-row-sub">{course.instructor}</span>
                                </span>
                                <span className="sv-meter">
                                  <span className="sv-meter-track">
                                    <span
                                      className={`sv-meter-fill${
                                        pct >= 100 ? ' is-done' : pct === 0 ? ' is-idle' : ''
                                      }`}
                                      style={{ width: `${pct}%` }}
                                    />
                                  </span>
                                  <span className="sv-meter-num">{pct}%</span>
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                </div>
              </div>
            )}

            {activeTab === 'batches' && (
              <section className="td-tbl td-tbl--svbatches">
                <header className="td-tbl-head">
                  <div>
                    <h5 className="td-tbl-title">Batches</h5>
                    <p className="td-tbl-sub">
                      {batchCount} batch{batchCount === 1 ? '' : 'es'}
                      {' · '}
                      {activeCount} running · {doneCount} completed
                    </p>
                  </div>
                </header>

                {batches.length === 0 ? (
                  <p className="td-tbl-empty">This student is not in any batch yet.</p>
                ) : (
                  <>
                    {/* Five labels against five tracks in --svbatches. */}
                    <div className="td-tbl-cols">
                      <span>Batch</span>
                      <span>Course</span>
                      <span>Mode</span>
                      <span>Join link</span>
                      <span>Attended</span>
                    </div>

                    <div className="td-tbl-rows">
                      {batches.map((batch) => {
                        const state = batch.state;
                        return (
                          <div
                            className="td-tbl-row td-tbl-row--open"
                            key={batch.id || batch._id}
                            role="button"
                            tabIndex={0}
                            title={`Open ${batch.name || 'batch'}`}
                            onClick={() => openEntity('batch', batch.id || batch._id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openEntity('batch', batch.id || batch._id);
                              }
                            }}
                          >
                            <div className="td-tbl-name">
                              <b>{batch.name}</b>
                              <small>
                                {dayOf(batch.startTime)} – {dayOf(batch.endTime)}
                              </small>
                            </div>
                            <div className="td-tbl-name">
                              <b>{batch.courseName}</b>
                              <small>{batch.trainerName}</small>
                            </div>
                            <span
                              className={`td-tbl-pill${
                                state === 'live' ? ' td-tbl-pill--live' : ''
                              }`}
                            >
                              {batch.modeLabel}
                            </span>
                            {/* The same control the student's own card
                                carries, in the same house style. The click
                                is stopped here: the row opens the batch,
                                and a link inside it must not do both. The
                                full address is on the title, so it can be
                                read and copied without opening anything. */}
                            {batch.joinLink ? (
                              <a
                                className="ln-act ln-act--go sv-join"
                                href={batch.joinLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={batch.joinLink}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Video size={15} aria-hidden="true" />
                                Join
                              </a>
                            ) : (
                              <span className="sv-join sv-join--none">No link</span>
                            )}
                            <span className="td-tbl-num">{batch.attendanceCount ?? 0}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>
            )}

            {activeTab === 'courses' && (
              <section className="td-tbl td-tbl--svcourses">
                <header className="td-tbl-head">
                  <div>
                    <h5 className="td-tbl-title">Courses</h5>
                    <p className="td-tbl-sub">
                      {courseCount} enrolled · progress is measured against the
                      batch window
                    </p>
                  </div>
                </header>

                {enrolledCourses.length === 0 ? (
                  <p className="td-tbl-empty">
                    This student is not currently enrolled in any courses.
                  </p>
                ) : (
                  <>
                    {/* Three labels against three tracks in --svcourses. */}
                    <div className="td-tbl-cols">
                      <span>Course</span>
                      <span>Progress</span>
                      <span>State</span>
                    </div>

                    <div className="td-tbl-rows">
                      {enrolledCourses.map((course, i) => {
                        const pct = course.progress;
                        // Only the ones that kept an id can be opened --
                        // a course assembled purely from a batch's name
                        // has nothing to open.
                        const openable = Boolean(course.courseId);
                        return (
                          <div
                            className={`td-tbl-row${openable ? ' td-tbl-row--open' : ''}`}
                            key={course.courseId || `${course.courseName}-${i}`}
                            {...(openable
                              ? {
                                  role: 'button',
                                  tabIndex: 0,
                                  title: `Open ${course.courseName || 'course'}`,
                                  onClick: () => openEntity('course', course.courseId),
                                  onKeyDown: (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      openEntity('course', course.courseId);
                                    }
                                  },
                                }
                              : {})}
                          >
                            <div className="td-tbl-name">
                              <b>{course.courseName}</b>
                              <small>{course.instructor}</small>
                            </div>
                            <span className="sv-meter">
                              <span className="sv-meter-track">
                                <span
                                  className={`sv-meter-fill${
                                    pct >= 100 ? ' is-done' : pct === 0 ? ' is-idle' : ''
                                  }`}
                                  style={{ width: `${pct}%` }}
                                />
                              </span>
                              <span className="sv-meter-num">{pct}%</span>
                            </span>
                            <span
                              className={`td-tbl-pill${
                                course.status === 'in-progress' ? ' td-tbl-pill--live' : ''
                              }`}
                            >
                              {course.status === 'completed'
                                ? 'Completed'
                                : course.status === 'in-progress'
                                ? 'In progress'
                                : 'Enrolled'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>
            )}

            {activeTab === 'upcoming' && (
              <section className="td-tbl td-tbl--svupcoming">
                <header className="td-tbl-head">
                  <div>
                    <h5 className="td-tbl-title">Upcoming batches</h5>
                    <p className="td-tbl-sub">
                      {upcomingCount} scheduled · soonest first
                    </p>
                  </div>
                </header>

                {upcoming.length === 0 ? (
                  <p className="td-tbl-empty">Nothing scheduled.</p>
                ) : (
                  <>
                    {/* Four labels against four tracks in --svupcoming. */}
                    <div className="td-tbl-cols">
                      <span>Batch</span>
                      <span>Starts</span>
                      <span>Mode</span>
                      <span>Trainer</span>
                    </div>

                    <div className="td-tbl-rows">
                      {upcoming.map((batch) => (
                        <div
                          className="td-tbl-row td-tbl-row--open"
                          key={batch.id || batch._id}
                          role="button"
                          tabIndex={0}
                          title={`Open ${batch.name || 'batch'}`}
                          onClick={() => openEntity('batch', batch.id || batch._id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openEntity('batch', batch.id || batch._id);
                            }
                          }}
                        >
                          <div className="td-tbl-name">
                            <b>{batch.name}</b>
                            <small>{batch.courseName}</small>
                          </div>
                          <div className="td-tbl-name">
                            <b>{dayOf(batch.startTime)}</b>
                            <small>{timeOf(batch.startTime)}</small>
                          </div>
                          <span className="td-tbl-pill">{batch.modeLabel}</span>
                          <div className="td-tbl-name">
                            <b>{batch.trainerName}</b>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </section>
            )}

            {activeTab === 'requests' && (
              <BatchChangeRequestsCard
                studentId={studentId}
                myBatches={batches}
                refreshSignal={requestsRefreshSignal}
                skin="staff"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* =============================================================
   STUDENT VIEW

   LinkedIn's profile page, read as a dashboard. The left rail is the
   student and the numbers that describe them; the centre is the feed,
   where a batch is a post written by its trainer; the right rail is
   what happens next and what they could join.
   ============================================================= */
// No section of the student page has answered yet.
const NOT_READY = { batches: false, attendance: false, courses: false, suggested: false };

/* One figure in the student's own profile card. A button when it opens
   something, a plain pair of spans when it does not. A `value` of null
   means its section has not answered: a shimmer, never a 0, and nothing
   to open yet. */
const MeStat = ({ label, value, onOpen }) =>
  value === null ? (
    <li>
      <span className="ln-me-stat-label">{label}</span>
      <span className="ln-me-stat-value">
        <Skeleton shape="title" width="40" />
      </span>
    </li>
  ) : (
    <MeStatLoaded label={label} value={value} onOpen={onOpen} />
  );

const MeStatLoaded = ({ label, value, onOpen }) => (
  <li>
    {onOpen ? (
      <button
        type="button"
        className="ln-me-stat-open"
        onClick={onOpen}
        title={`Open ${label.toLowerCase()}`}
      >
        <span className="ln-me-stat-label">{label}</span>
        <span className="ln-me-stat-value">{value}</span>
      </button>
    ) : (
      <>
        <span className="ln-me-stat-label">{label}</span>
        <span className="ln-me-stat-value">{value}</span>
      </>
    )}
  </li>
);

const StudentFeedView = ({
  loading,
  // Which sections have answered -- see StudentDashboard2. Anything not
  // yet answered draws as a skeleton.
  ready = NOT_READY,
  dashboardData,
  enrolledCourses,
  studentName,
  studentEmail,
  studentId,
  requestsRefreshSignal,
}) => {
  const navigate = useNavigate();
  // Same Waypoint the staff view uses, so a student opening their own
  // batch and a counsellor opening it see the same thing.
  const { open: openEntity } = useWaypoint();

  // A tab on this student, when there is anything on it to look at.
  const openMyTab = (tab, enabled) =>
    studentId && enabled ? () => openEntity('student', studentId, { tab }) : null;
  const [search, setSearch] = useState('');
  const [newestFirst, setNewestFirst] = useState(true);
  const [shown, setShown] = useState(4);
  /* The whole list, in a popup. The feed shows its first cards and no
     more: "Show more" used to append four cards at a time, so a student
     with a long history scrolled past screens of batch cards to reach
     their courses and the blog below them. */
  const [allBatchesOpen, setAllBatchesOpen] = useState(false);
  useEffect(() => {
    if (!allBatchesOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setAllBatchesOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [allBatchesOpen]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [blogsShown, setBlogsShown] = useState(3);
  const blogsAsked = useRef(false);

  /* The blog roll, fetched once. It is the tail of the feed, so a
     failure is not worth an error state — the section simply does not
     appear. */
  useEffect(() => {
    if (blogsAsked.current) return;
    blogsAsked.current = true;
    (async () => {
      try {
        const res = await getBlogs(9);
        setBlogs(Array.isArray(res?.data) ? res.data : []);
      } catch (_) {
        setBlogs([]);
      }
    })();
  }, []);

  /* Escape closes the lightbox, the same way the trainer dashboard's two
     sheets close. Bound only while it is open so the page is not holding
     a key listener for a video nobody has opened. */
  useEffect(() => {
    if (!videoOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setVideoOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [videoOpen]);

  const batches = useMemo(
    () => (Array.isArray(dashboardData?.batches) ? dashboardData.batches : []),
    [dashboardData],
  );
  const upcoming = useMemo(
    () => (Array.isArray(dashboardData?.upcomingBatches) ? dashboardData.upcomingBatches : []),
    [dashboardData],
  );
  const suggested = useMemo(
    () => (Array.isArray(dashboardData?.suggestedBatches) ? dashboardData.suggestedBatches : []),
    [dashboardData],
  );

  const attendance = Number(dashboardData?.attendancePercentage) || 0;
  const batchCount = dashboardData?.totalBatches ?? 0;
  const upcomingCount = dashboardData?.upcomingBatchesCount ?? 0;
  const courseCount = dashboardData?.totalCourseEnrolled ?? 0;

  /* The banner at the top of the column -- whatever is running, or else
     whatever starts soonest -- chosen by the API. */
  const nextUp = dashboardData?.nextUp || null;

  /* The feed, searched and ordered by the API: running before starting
     soon before completed, and inside each band by start date. The
     dashboard payload carries the default order (newest first, no
     search) as feedBatches, so first paint costs no second request; a
     search or a flip of the sort asks /studentdashboard/feed, debounced
     while typing, and a slow reply cannot land over a newer one. */
  const [feedResult, setFeedResult] = useState({ rows: [], matched: 0 });
  const feedSeq = useRef(0);
  useEffect(() => {
    const term = search.trim();
    const seq = ++feedSeq.current;

    if (!term && newestFirst) {
      setFeedResult({
        rows: Array.isArray(dashboardData?.feedBatches) ? dashboardData.feedBatches : [],
        matched: dashboardData?.totalBatches ?? 0,
      });
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        // A search is not an action; keep the Working overlay off it.
        silenceFor();
        const res = await getStudentDashboardFeed({
          search: term,
          order: newestFirst ? 'newest' : 'oldest',
        });
        if (seq !== feedSeq.current) return;
        setFeedResult({
          rows: Array.isArray(res?.data?.batches) ? res.data.batches : [],
          matched: res?.data?.matchedCount ?? 0,
        });
      } catch (_) {
        // Leave the rows on screen; the next keystroke asks again.
      }
    }, term ? 350 : 0);

    return () => clearTimeout(timer);
  }, [dashboardData, search, newestFirst]);
  const feed = feedResult.rows;

  /* The batch whose detail sheet is open, or null. Phones only -- the
     button that sets it is display:none above 768px. */
  const [sheetBatch, setSheetBatch] = useState(null);

  /* A phone rotated to landscape, or a desktop window dragged wider,
     crosses the breakpoint with the sheet still mounted over a layout
     that has just put every fact back on the card behind it. */
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!isMobile) setSheetBatch(null);
  }, [isMobile]);

  /* Escape closes it, and the page behind it stops scrolling. Without
     the lock, dragging the sheet scrolls the feed underneath on iOS and
     the sheet appears to slide off its own content. */
  useEffect(() => {
    if (!sheetBatch) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSheetBatch(null);
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [sheetBatch]);

  const openChat = (chatGroupId) => {
    if (chatGroupId) {
      navigate('/chat', { state: { selectedChatId: chatGroupId } });
      return;
    }
    toast.warning('A chat group has not been linked to this batch yet.');
  };

  /* Every destination the sidebar's Student Module offers, plus the two
     this card had that it never did.

     The rail used to hide that module from students on a desktop, on
     the argument that this card was the same navigation a few hundred
     pixels to the left, which made this list their only way in. It no
     longer hides it — the module is in the rail for students at every
     width — so this card is a shortcut to it rather than a substitute
     for it, and a gap here is an inconvenience rather than a dead end.

     Still worth keeping in step with MenuList's 'student-module' block
     in layouts/Menu.jsx. The paths are that block's, not guesses:
     /job-section and /attendance-calendar below are what the module
     links to, and are NOT the same pages as the /job-board and
     /student-calendar this card used to send people to. Both pairs are
     kept — they are four different screens. */
  const SHORTCUTS = [
    { icon: User, label: 'My profile', to: '/student-profile' },
    { icon: TrendingUp, label: 'Progress card', to: '/progress-card' },
    { icon: ContactRound, label: 'ID card', to: '/student-id-card' },
    { icon: Award, label: 'Certificates', to: '/certificate' },
    { icon: Layers, label: 'Suggested batches', to: '/suggested-batches' },
    { icon: ClipboardList, label: 'My quizzes', to: '/student-quizzes' },
    /* /mock-interviews is the ADMIN management screen. Its guard calls
       navigate("/") for anyone without mock-interviews:view, which for a
       student bounced them straight back to this dashboard. The student
       page is /student-mock-interview — the one the sidebar's Student
       Module points at. */
    { icon: FileText, label: 'Mock interviews', to: '/student-mock-interview' },
    { icon: Trophy, label: 'Ranking', to: '/ranking-dashboard' },
    { icon: Briefcase, label: 'Job board', to: '/job-board' },
    { icon: Briefcase, label: 'Job openings', to: '/job-section' },
    { icon: Calendar, label: 'My calendar', to: '/student-calendar' },
    { icon: Calendar, label: 'Attendance', to: '/attendance-record' },
    /* The module's last entry is an external feedback site, so it leaves
       the app rather than routing inside it. */
    {
      icon: MessageSquareHeart,
      label: 'Feedback',
      to: 'https://feedback.sevenmentor.com/feedback',
      external: true,
    },
  ];

  return (
    <div className="dashboard-wrapper">
      <div className="ln">
        <div className="ln-grid">
          {/* ── Hero: the profile band, full width ───────────────── */}
          {/* Out of the left rail and across the whole grid. It is the
              one card on this page that is ABOUT the reader rather than
              a list of things for them to do, and at 225px in the rail
              it read as one more widget. LinkedIn puts the same card at
              the top at full width with a deep cover behind it; the
              layout below does the same, and .ln-links and .ln-desks
              stay in the rail where they belong. */}
          <section className="ln-card ln-me">
            <div className="ln-me-cover" aria-hidden="true" />
            <div className="ln-me-body">
              {/* The name arrives with the batches, the headline with the
                  courses -- until then, shimmer rather than "Student" and
                  an "S" that are nobody's. */}
              <span className="ln-me-avatar">
                {ready.batches ? initialsOf(studentName, 'S') : ''}
              </span>
              <h1 className="ln-me-name">
                {ready.batches ? studentName || 'Student' : <Skeleton shape="title" width="40" />}
              </h1>
              <p className="ln-me-headline">
                {!ready.courses ? (
                  <Skeleton width="55" />
                ) : enrolledCourses.length > 0 ? (
                  `Learning ${enrolledCourses[0].courseName} at SevenMentor`
                ) : (
                  'Student at SevenMentor'
                )}
              </p>
              {studentEmail && <p className="ln-me-mail">{studentEmail}</p>}
            </div>

            {/* The same four figures the staff view carries, opening the
                same tabs on the same student. A student reading their own
                record and a counsellor reading it get the same popup --
                which is the point of routing both through Waypoint rather
                than each view growing its own. */}
            <ul className="ln-me-stats">
              <MeStat
                label="Courses enrolled"
                value={ready.courses ? dashboardData?.totalCourseEnrolled ?? 0 : null}
                onOpen={openMyTab('courses', (dashboardData?.totalCourseEnrolled ?? 0) > 0)}
              />
              <MeStat
                label="Attendance"
                value={ready.attendance ? `${attendance}%` : null}
                onOpen={openMyTab('attendance', true)}
              />
              {/* A total, not a collection -- the attendance tab already
                  reports it, so opening from here would land on a screen
                  showing a different number. */}
              <MeStat
                label="Classes attended"
                value={ready.attendance ? dashboardData?.classesAttended ?? 0 : null}
              />
              <MeStat
                label="Batches"
                value={ready.batches ? batchCount : null}
                onOpen={openMyTab('batches', batchCount > 0)}
              />
            </ul>
          </section>

          {/* ── Left rail: what this student can go and do ───────── */}
          <aside className="ln-rail ln-rail--left">
            <nav className="ln-card ln-links">
              {SHORTCUTS.map(({ icon: Icon, label, to, external }) =>
                /* An external destination is a real link, not a router
                   push: navigate() would treat the https:// as an app
                   path and land on the 404 page. It also opens in its own
                   tab, so a student filling in feedback does not lose the
                   dashboard behind it. */
                external ? (
                  <a
                    key={to}
                    className="ln-link"
                    href={to}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon size={16} aria-hidden="true" />
                    {label}
                    <ExternalLink size={12} aria-hidden="true" />
                  </a>
                ) : (
                  <button
                    type="button"
                    key={to}
                    className="ln-link"
                    onClick={() => navigate(to)}
                  >
                    <Icon size={16} aria-hidden="true" />
                    {label}
                  </button>
                )
              )}
            </nav>

            {/* The contact desks, moved out of the full-width strip at the
                foot of the page into the rail. Same four desks as
                ContactInfo.jsx — they share one list so the numbers
                cannot drift apart. */}
            {/* ln-desks names the card so the phone layout can order it:
                reference material belongs under the feed there, not
                between the student and their own batches. */}
            <section className="ln-card ln-desks">
              <header className="ln-card-head">
                <h2 className="ln-card-title">Contacts</h2>
              </header>
              <p className="ln-card-sub">Who to call about what</p>

              <ul className="ln-contacts">
                {CONTACTS.map(({ key, label, tel, telHref, email, icon: Icon, color }) => (
                  <li key={key}>
                    <span className="ln-contact-desk">
                      <Icon size={14} color={color} aria-hidden="true" />
                      {label}
                    </span>
                    <a className="ln-contact-link" href={telHref}>
                      <Phone size={12} aria-hidden="true" />
                      <span className="ln-contact-text">{tel}</span>
                    </a>
                    <a className="ln-contact-link" href={`mailto:${email}`}>
                      <Mail size={12} aria-hidden="true" />
                      <span className="ln-contact-text">{email}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </aside>

          {/* ── Centre: the feed ─────────────────────────────────── */}
          <main className="ln-main">
            {nextUp && (
              <div className="ln-sec">
                <h2 className="ln-sec-title">
                  {nextUp.live ? 'Happening now' : 'Up next'}
                </h2>
                <span className="ln-sec-line" aria-hidden="true" />
              </div>
            )}

            {nextUp && (
              <section className="ln-card ln-next">
                <span className="ln-next-mark">
                  <Video size={22} aria-hidden="true" />
                </span>
                <div className="ln-next-main">
                  <span className="ln-next-label">
                    {nextUp.live ? 'In session now' : 'Starting soon'}
                  </span>
                  <p className="ln-next-title">{nextUp.batch.name}</p>
                  <p className="ln-next-when">
                    {nextUp.batch.courseName} · {timeOf(nextUp.batch.startTime)} –{' '}
                    {timeOf(nextUp.batch.endTime)}
                  </p>
                </div>
                {nextUp.batch.meetingLink && (
                  <a
                    className="ln-follow"
                    href={nextUp.batch.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Video size={14} aria-hidden="true" />
                    Join
                  </a>
                )}
              </section>
            )}

            {/* The header names the run of cards below it and carries the
                control that reorders it, so the search and the sort are
                plainly scoped to batches rather than to the whole page. */}
            <div className="ln-sec">
              <h2 className="ln-sec-title">Your batches</h2>
              {!loading && <span className="ln-sec-count">{feedResult.matched}</span>}
              <span className="ln-sec-line" aria-hidden="true" />
              <button
                type="button"
                className="ln-sort-btn"
                onClick={() => setNewestFirst((on) => !on)}
              >
                {newestFirst ? <SortDesc size={13} /> : <SortAsc size={13} />}
                {newestFirst ? 'Newest' : 'Oldest'}
              </button>
            </div>

            <div className="ln-card ln-find">
              <Search size={16} aria-hidden="true" />
              <input
                type="text"
                id="sv-batch-search"
                name="batchSearch"
                value={search}
                placeholder="Search batches by name, course or trainer"
                aria-label="Search your batches"
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShown(4);
                }}
              />
            </div>

            {loading ? (
              /* Three batch cards, shimmering, in the cards' own frame. */
              <SkeletonGroup label="Loading your batches" className="lms-skel-stack">
                {[0, 1, 2].map((i) => (
                  <div className="ln-card ln-skel lms-skel-stack" key={i}>
                    <Skeleton width="25" />
                    <Skeleton shape="title" width="70" />
                    <Skeleton width="55" />
                  </div>
                ))}
              </SkeletonGroup>
            ) : feed.length === 0 ? (
              <div className="ln-card">
                <div className="ln-empty">
                  <span className="ln-empty-mark">
                    <CalendarX size={22} aria-hidden="true" />
                  </span>
                  <h2 className="ln-empty-title">
                    {batchCount === 0 ? 'No batches yet' : 'Nothing matched'}
                  </h2>
                  <p className="ln-empty-text">
                    {batchCount === 0
                      ? 'Once you are enrolled in a batch it will appear here, with the link to join each class.'
                      : 'No batch matches that search. Try the trainer or course name instead.'}
                  </p>
                </div>
              </div>
            ) : (
              feed.slice(0, shown).map((batch) => {
                const state = batch.state;
                const pct = batch.progress;
                const hasAction =
                  batch.meetingLink ||
                  batch.chatGroupId ||
                  batch.studyMaterialLink ||
                  batch.recordedLectureLink;

                return (
                  <article className="ln-card" key={batch.id || batch._id}>
                    {/* The batch is the subject of the card, so the batch is
                        the headline. It used to be the trainer, with the
                        batch name buried in the fact list — which is what
                        made one card hard to tell from the next. */}
                    {/* Only the head opens the batch. The card below it
                        holds its own links -- meeting, materials, chat --
                        and making the whole card a target would swallow
                        them. */}
                    <div className="ln-post-head">
                      <span className="ln-ava">{initialsOf(batch.name, 'B')}</span>
                      <div className="ln-post-id">
                        <h3 className="ln-post-title">
                          <button
                            type="button"
                            className="ln-post-open"
                            onClick={() => openEntity('batch', batch.id || batch._id)}
                          >
                            {batch.name}
                          </button>
                        </h3>
                        <p className="ln-post-sub">{batch.courseName}</p>
                      </div>
                      <span className={`ln-chip ln-chip--${state}`}>{STATE_WORD[state]}</span>
                    </div>

                    <div className="ln-post-body">
                      <dl className="ln-facts">
                        {factsOf(batch).map(({ key, icon: FactIcon, label, value }) => (
                          <div key={key}>
                            <dt>
                              <FactIcon size={12} aria-hidden="true" />
                              {label}
                            </dt>
                            <dd>{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>

                    <div className="ln-prog">
                      <div className="ln-prog-top">
                        <span className="ln-prog-label">Batch progress</span>
                        <span className="ln-prog-num">{pct}%</span>
                      </div>
                      <div className="ln-prog-track">
                        <span
                          className={`ln-prog-fill${
                            pct >= 100 ? ' is-done' : pct === 0 ? ' is-idle' : ''
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>

                    {/* Phones only (CSS decides, not JS). On a phone the six
                        facts and the action strip together make a card two
                        screens tall, and four of them make the feed
                        unscrollable. Both move into the sheet and this is
                        what opens it; the desktop card is unchanged. */}
                    <button
                      type="button"
                      className="ln-view"
                      onClick={() => setSheetBatch(batch)}
                    >
                      View details
                      <ChevronRight size={16} aria-hidden="true" />
                    </button>

                    {hasAction && (
                      <div className="ln-acts">
                        {batch.meetingLink && (
                          <a
                            className="ln-act ln-act--go"
                            href={batch.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Video size={16} aria-hidden="true" />
                            Join class
                          </a>
                        )}
                        {batch.chatGroupId && (
                          <button
                            type="button"
                            className="ln-act"
                            onClick={() => openChat(batch.chatGroupId)}
                          >
                            <MessageCircle size={16} aria-hidden="true" />
                            Chat
                          </button>
                        )}
                        {batch.studyMaterialLink && (
                          <a
                            className="ln-act"
                            href={batch.studyMaterialLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download size={16} aria-hidden="true" />
                            Materials
                          </a>
                        )}
                        {batch.recordedLectureLink && (
                          <a
                            className="ln-act"
                            href={batch.recordedLectureLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <PlayCircle size={16} aria-hidden="true" />
                            Recordings
                          </a>
                        )}
                      </div>
                    )}
                  </article>
                );
              })
            )}

            {feed.length > shown && (
              <button
                type="button"
                className="ln-more"
                onClick={() => setAllBatchesOpen(true)}
              >
                View all batches ({feedResult.matched || feed.length})
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            )}

            {/* Every batch in the feed, as the house view popup: the list
                is the view_popup_rules table, a row opens its batch, and
                the popup closes before Waypoint opens so two dialogs never
                stack. Same search and order as the feed behind it -- it is
                that list, not a second question to the API. */}
            {allBatchesOpen &&
              createPortal(
                <div
                  className="pg-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="sd-all-batches-title"
                  onMouseDown={(e) => {
                    if (e.target === e.currentTarget) setAllBatchesOpen(false);
                  }}
                >
                  <div className="pg-modal-panel pg-modal-panel--wide">
                    <header className="pgm-head">
                      <div className="pgm-head-main">
                        <h2 className="pgm-batch" id="sd-all-batches-title">
                          Your batches
                        </h2>
                        <dl className="pgm-ident">
                          <div className="pgm-ident-row">
                            <dt>Batches</dt>
                            <dd>{feedResult.matched || feed.length}</dd>
                          </div>
                          {search.trim() && (
                            <div className="pgm-ident-row">
                              <dt>Search</dt>
                              <dd>{search.trim()}</dd>
                            </div>
                          )}
                          <div className="pgm-ident-row">
                            <dt>Order</dt>
                            <dd>{newestFirst ? 'Newest first' : 'Oldest first'}</dd>
                          </div>
                        </dl>
                      </div>
                      <button
                        type="button"
                        className="pg-modal-close"
                        onClick={() => setAllBatchesOpen(false)}
                        aria-label="Close"
                      >
                        &times;
                      </button>
                    </header>

                    <div className="pg-modal-body">
                      <section className="pgm-section">
                        <h3 className="pgm-section-title">
                          Batches
                          <span className="pgm-section-count">{feed.length}</span>
                        </h3>
                        <div className="pgm-tablescroll">
                          <table className="pgm-table pgm-table--values">
                            <thead>
                              <tr>
                                <th className="pgm-num">#</th>
                                <th>Batch</th>
                                <th>Status</th>
                                <th className="pgm-num">Progress</th>
                              </tr>
                            </thead>
                            <tbody>
                              {feed.map((batch, i) => {
                                const id = batch.id || batch._id;
                                const open = () => {
                                  setAllBatchesOpen(false);
                                  openEntity('batch', id);
                                };
                                return (
                                  <tr
                                    key={id || i}
                                    className="pgm-row--open"
                                    tabIndex={0}
                                    role="button"
                                    aria-label={`Open batch ${batch.name || ''}`.trim()}
                                    onClick={open}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        open();
                                      }
                                    }}
                                  >
                                    <td className="pgm-num">{i + 1}</td>
                                    <td>
                                      <span className="pgm-person-id">
                                        <b className="pgm-person-name">{batch.name}</b>
                                        <span className="pgm-person-mail">
                                          {batch.courseName}
                                          {batch.trainerName ? ` · ${batch.trainerName}` : ''}
                                        </span>
                                      </span>
                                    </td>
                                    <td>
                                      <span
                                        className={`pgm-pill${batch.state === 'live' ? ' pgm-pill--good' : ''}`}
                                      >
                                        {STATE_WORD[batch.state]}
                                      </span>
                                    </td>
                                    <td className="pgm-num">{batch.progress}%</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {feedResult.matched > feed.length && (
                          <p className="pgm-empty">
                            Showing {feed.length} of {feedResult.matched}. Search to narrow the list.
                          </p>
                        )}
                      </section>
                    </div>

                    <footer className="pg-modal-foot">
                      <div className="pgm-actions" />
                      <button
                        type="button"
                        className="pg-btn pg-btn--main"
                        onClick={() => setAllBatchesOpen(false)}
                      >
                        Close
                      </button>
                    </footer>
                  </div>
                </div>,
                document.body,
              )}

            {/* Courses run alongside batches in the same column, written the
                same way. Until the courses answer, two cards shimmer in the
                course card's own frame; with none, the section is not drawn. */}
            {!ready.courses && (
              <SkeletonGroup label="Loading your courses" className="lms-skel-stack">
                {[0, 1].map((i) => (
                  <div className="ln-card ln-skel" key={i}>
                    <div className="lms-skel-row">
                      <Skeleton shape="mark" />
                      <div className="lms-skel-grow lms-skel-stack">
                        <Skeleton shape="title" width="55" />
                        <Skeleton width="40" />
                      </div>
                      <Skeleton shape="pill" />
                    </div>
                  </div>
                ))}
              </SkeletonGroup>
            )}

            {ready.courses && enrolledCourses.length > 0 && (
              <>
                <div className="ln-sec">
                  <h2 className="ln-sec-title">Your courses</h2>
                  <span className="ln-sec-count">{courseCount}</span>
                  <span className="ln-sec-line" aria-hidden="true" />
                </div>

                {/* No progress bar. The API's course figure is the average of
                    EVERY batch of the course across the system, not this
                    student's own, so a student two weeks in could read 85%. */}
                {enrolledCourses.map((course, i) => {
                  const word =
                    course.status === 'completed'
                      ? 'done'
                      : course.status === 'in-progress'
                      ? 'live'
                      : 'soon';

                  return (
                    <article className="ln-card" key={`${course.courseName}-${i}`}>
                      <div className="ln-post-head">
                        <span className="ln-ava ln-ava--course">
                          <GraduationCap size={22} aria-hidden="true" />
                        </span>
                        <div className="ln-post-id">
                          <h3 className="ln-post-title">{course.courseName}</h3>
                          <p className="ln-post-sub">Trainer · {course.instructor}</p>
                        </div>
                        <span className={`ln-chip ln-chip--${word}`}>
                          {course.status === 'completed'
                            ? 'Completed'
                            : course.status === 'in-progress'
                            ? 'In progress'
                            : 'Enrolled'}
                        </span>
                      </div>
                    </article>
                  );
                })}
              </>
            )}

            {/* The blog roll closes the feed. Each post is a card like
                everything above it rather than the three-across carousel
                that used to sit on white below the whole dashboard. */}
            {blogs.length > 0 && (
              <>
                <div className="ln-sec">
                  <h2 className="ln-sec-title">From the blog</h2>
                  <span className="ln-sec-count">{blogs.length}</span>
                  <span className="ln-sec-line" aria-hidden="true" />
                </div>

                {blogs.slice(0, blogsShown).map((blog) => {
                  const excerpt = String(blog.content1 || '')
                    .replace(/<[^>]+>/g, '')
                    .trim()
                    .slice(0, 180);

                  return (
                    <article className="ln-card" key={blog.id}>
                      <button
                        type="button"
                        className="ln-blog"
                        onClick={() =>
                          window.open(
                            `https://www.sevenmentor.com/${blog.slug}`,
                            '_blank',
                            'noopener,noreferrer',
                          )
                        }
                      >
                        {blog.image && (
                          <img
                            className="ln-blog-img"
                            src={blog.image}
                            alt={blog.imageAlt || ''}
                            loading="lazy"
                          />
                        )}
                        <span className="ln-blog-body">
                          <span className="ln-blog-title">{blog.title}</span>
                          {blog.authorName && (
                            <span className="ln-blog-by">By {blog.authorName}</span>
                          )}
                          {excerpt && <span className="ln-blog-text">{excerpt}…</span>}
                          <span className="ln-blog-more">
                            Read more
                            <ExternalLink size={13} aria-hidden="true" />
                          </span>
                        </span>
                      </button>
                    </article>
                  );
                })}

                {blogs.length > blogsShown && (
                  <button
                    type="button"
                    className="ln-more"
                    onClick={() => setBlogsShown((n) => n + 3)}
                  >
                    Show more posts ({blogs.length - blogsShown})
                    <ChevronDown size={16} aria-hidden="true" />
                  </button>
                )}
              </>
            )}
          </main>

          {/* ── Right rail: what is next ─────────────────────────── */}
          <aside className="ln-rail ln-rail--right">
            <section className="ln-card">
              <header className="ln-card-head">
                <h2 className="ln-card-title">Upcoming classes</h2>
              </header>
              <p className="ln-card-sub">
                {!ready.batches ? (
                  <Skeleton width="40" />
                ) : upcomingCount === 0 ? (
                  'Nothing on the calendar'
                ) : (
                  `${upcomingCount} scheduled`
                )}
              </p>

              {!ready.batches ? (
                /* Three rows in the list's own shape: mark, name, date. */
                <SkeletonGroup label="Loading upcoming classes" className="lms-skel-stack">
                  {[0, 1, 2].map((i) => (
                    <div className="lms-skel-row" key={i}>
                      <Skeleton shape="mark" />
                      <div className="lms-skel-grow lms-skel-stack">
                        <Skeleton width="70" />
                        <Skeleton width="40" />
                      </div>
                    </div>
                  ))}
                </SkeletonGroup>
              ) : upcoming.length === 0 ? (
                <div className="ln-empty">
                  <span className="ln-empty-mark">
                    <CalendarX size={20} aria-hidden="true" />
                  </span>
                  <p className="ln-empty-text">
                    Nothing scheduled yet. New batches show up here as soon as they are
                    assigned.
                  </p>
                </div>
              ) : (
                <ul className="ln-mini">
                  {upcoming.slice(0, 4).map((batch) => (
                    <li key={batch.id || batch._id}>
                      <span className="ln-mini-mark">{initialsOf(batch.name, 'B')}</span>
                      <div className="ln-mini-main">
                        {/* The name opens the batch; the two lines under
                            it stay plain text, so the target is the thing
                            being named rather than the whole tile. */}
                        <div className="ln-mini-name">
                          <button
                            type="button"
                            className="ln-mini-open"
                            onClick={() => openEntity('batch', batch.id || batch._id)}
                          >
                            {batch.name}
                          </button>
                        </div>
                        <div className="ln-mini-sub">
                          {dayOf(batch.startTime)} · {timeOf(batch.startTime)}
                        </div>
                        <div className="ln-mini-sub">{batch.trainerName}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                className="ln-card-foot"
                onClick={() => navigate('/student-calendar')}
              >
                See my calendar
              </button>
            </section>

            {/* Suggestions answer on their own; until they do, the card's
                frame shimmers. With none, the card is not drawn at all. */}
            {!ready.suggested && (
              <section className="ln-card">
                <SkeletonGroup label="Loading suggested batches" className="lms-skel-stack">
                  <Skeleton shape="title" width="55" />
                  {[0, 1].map((i) => (
                    <div className="lms-skel-row" key={i}>
                      <Skeleton shape="mark" />
                      <div className="lms-skel-grow lms-skel-stack">
                        <Skeleton width="70" />
                        <Skeleton width="40" />
                      </div>
                    </div>
                  ))}
                </SkeletonGroup>
              </section>
            )}

            {ready.suggested && suggested.length > 0 && (
              <section className="ln-card">
                <header className="ln-card-head">
                  <h2 className="ln-card-title">Batches you could join</h2>
                </header>
                <p className="ln-card-sub">Same course, a different schedule</p>

                <ul className="ln-mini">
                  {suggested.slice(0, 3).map((batch) => (
                    <li key={batch.id || batch._id}>
                      <span className="ln-mini-mark">{initialsOf(batch.name, 'B')}</span>
                      <div className="ln-mini-main">
                        <div className="ln-mini-name">{batch.name}</div>
                        <div className="ln-mini-sub">
                          {batch.courseName} · {batch.modeLabel}
                        </div>
                        <div className="ln-mini-sub">Starts {dayOf(batch.startTime)}</div>
                        <button
                          type="button"
                          className="ln-follow"
                          onClick={() => navigate('/suggested-batches')}
                        >
                          <Repeat size={13} aria-hidden="true" />
                          Request switch
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className="ln-card-foot"
                  onClick={() => navigate('/suggested-batches')}
                >
                  See all suggestions
                </button>
              </section>
            )}

            {studentId && (
              <BatchChangeRequestsCard
                studentId={studentId}
                myBatches={batches}
                refreshSignal={requestsRefreshSignal}
                skin="feed"
              />
            )}

            {/* The feedback banner that used to run full width under the
                dashboard. It keeps its own card here rather than becoming
                one more link in the footer. */}
            <section className="ln-card">
              <header className="ln-card-head">
                <h2 className="ln-card-title">Your feedback matters</h2>
              </header>
              <p className="ln-card-sub">
                Tell us how the course is going. It is read by the team that runs your
                batch.
              </p>
              <div className="ln-card-act">
                <a
                  className="ln-follow"
                  href="https://feedback.sevenmentor.com/feedback"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle size={13} aria-hidden="true" />
                  Give feedback
                </a>
              </div>
            </section>

            {/* The tour video, moved out of the full-width strip under the
                dashboard and into the rail beneath the feedback card. */}
            <section className="ln-card">
              <header className="ln-card-head">
                <h2 className="ln-card-title">Take a quick tour</h2>
              </header>
              <p className="ln-card-sub">A short walk through the portal</p>

              <button
                type="button"
                className="ln-video"
                onClick={() => setVideoOpen(true)}
                aria-label="Play the tour video"
              >
                <img
                  src={`https://img.youtube.com/vi/${TOUR_VIDEO_ID}/hqdefault.jpg`}
                  alt=""
                />
                <span className="ln-video-play">
                  <Play size={20} aria-hidden="true" />
                </span>
                <span className="ln-video-cap">
                  <span className="ln-video-title">Take a quick tour</span>
                  <span className="ln-video-sub">Tap to watch</span>
                </span>
              </button>
            </section>

            <div className="ln-foot">
              <button type="button" onClick={() => navigate('/help-desk')}>
                Help desk
              </button>
              <button type="button" onClick={() => navigate('/job-board')}>
                Jobs
              </button>
              <button type="button" onClick={() => navigate('/certificate')}>
                Certificates
              </button>
              <p className="ln-foot-note">SevenMentor · Learning Management System</p>
            </div>
          </aside>
        </div>

        {videoOpen && (
          /* The ground closes the lightbox; the frame swallows the click so
             tapping the video itself does not. */
          <div
            className="ln-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Tour video"
            onClick={() => setVideoOpen(false)}
          >
            <div className="ln-lightbox-frame" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="ln-lightbox-close"
                onClick={() => setVideoOpen(false)}
                aria-label="Close video"
              >
                <X size={18} aria-hidden="true" />
              </button>
              <iframe
                src={`https://www.youtube.com/embed/${TOUR_VIDEO_ID}?autoplay=1`}
                title="Tour video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* ── Batch detail, on a phone ──────────────────────────────
            Everything the card stops showing below 768px: the six facts
            and whatever links the batch has. Mounted only when a card
            asked for it, so the markup is not carried on a desktop. */}
        {sheetBatch && (
          <>
            <div
              className="ln-sheet-scrim"
              role="button"
              tabIndex={0}
              aria-label="Close batch details"
              onClick={() => setSheetBatch(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSheetBatch(null);
              }}
            />

            <section
              className="ln-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={`${sheetBatch.name} details`}
            >
              <span className="ln-sheet-grip" aria-hidden="true" />

              <header className="ln-sheet-head">
                <span className="ln-ava">{initialsOf(sheetBatch.name, 'B')}</span>
                <div className="ln-sheet-id">
                  <h3 className="ln-sheet-title">{sheetBatch.name}</h3>
                  <p className="ln-sheet-sub">{sheetBatch.courseName}</p>
                </div>
                <button
                  type="button"
                  className="ln-sheet-close"
                  onClick={() => setSheetBatch(null)}
                  aria-label="Close"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </header>

              <div className="ln-sheet-body">
                <span className={`ln-chip ln-chip--${sheetBatch.state}`}>
                  {STATE_WORD[sheetBatch.state]}
                </span>

                <dl className="ln-sheet-facts">
                  {factsOf(sheetBatch).map(({ key, icon: FactIcon, label, value }) => (
                    <div key={key}>
                      <dt>
                        <FactIcon size={13} aria-hidden="true" />
                        {label}
                      </dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="ln-sheet-prog">
                  <div className="ln-prog-top">
                    <span className="ln-prog-label">Batch progress</span>
                    <span className="ln-prog-num">{sheetBatch.progress}%</span>
                  </div>
                  <div className="ln-prog-track">
                    <span
                      className={`ln-prog-fill${
                        sheetBatch.progress >= 100
                          ? ' is-done'
                          : sheetBatch.progress === 0
                          ? ' is-idle'
                          : ''
                      }`}
                      style={{ width: `${sheetBatch.progress}%` }}
                    />
                  </div>
                </div>
              </div>

              {(sheetBatch.meetingLink ||
                sheetBatch.chatGroupId ||
                sheetBatch.studyMaterialLink ||
                sheetBatch.recordedLectureLink) && (
                <footer className="ln-sheet-acts">
                  {sheetBatch.meetingLink && (
                    <a
                      className="ln-act ln-act--go"
                      href={sheetBatch.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Video size={16} aria-hidden="true" />
                      Join class
                    </a>
                  )}
                  {sheetBatch.chatGroupId && (
                    <button
                      type="button"
                      className="ln-act"
                      onClick={() => {
                        const id = sheetBatch.chatGroupId;
                        setSheetBatch(null);
                        openChat(id);
                      }}
                    >
                      <MessageCircle size={16} aria-hidden="true" />
                      Chat
                    </button>
                  )}
                  {sheetBatch.studyMaterialLink && (
                    <a
                      className="ln-act"
                      href={sheetBatch.studyMaterialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download size={16} aria-hidden="true" />
                      Materials
                    </a>
                  )}
                  {sheetBatch.recordedLectureLink && (
                    <a
                      className="ln-act"
                      href={sheetBatch.recordedLectureLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <PlayCircle size={16} aria-hidden="true" />
                      Recordings
                    </a>
                  )}
                </footer>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

/* =============================================================
   THE PAGE

   One data layer, two renderers. Everything above the return is the
   fetch and the admin student-picker as they already were; the only
   change is that the viewer test now names staff rather than admins,
   because a trainer reading a student needs the staff view too.
   ============================================================= */
export function StudentDashboard2() {
  const [studentName, setStudentName] = useState('');
  const [loading, setLoading] = useState(true);
  /* Which of the four section requests has answered (or failed) for the
     current load. A section that has not is drawn as a skeleton -- never
     as a 0, a 0% or "Student", which read as real answers. */
  const [ready, setReady] = useState(NOT_READY);
  const [dashboardData, setDashboardData] = useState(null);
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  const { user: authUser } = useAuth();
  const location = useLocation();
  const [isStaff, setIsStaff] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState('');
  const [selectedStudentValue, setSelectedStudentValue] = useState(null);
  const [selectedStudentEmail, setSelectedStudentEmail] = useState('');
  // Bumped whenever a batch-change request is submitted from Suggested
  // Batches, so the Batch Change Requests list (a separate component with
  // its own fetch) picks up the new row without a full dashboard reload.
  const [requestsRefreshSignal, setRequestsRefreshSignal] = useState(0);

  // Pagination & infinite scroll state for the staff dropdown
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');

  const isFetchingRef = useRef(false);
  // Which load the section answers belong to; see fetchDashboardData.
  const loadSeqRef = useRef(0);
  const lastFetchedIdRef = useRef(null);
  const lastFetchedEmailRef = useRef(null);
  const isStaffRef = useRef(false);
  const authUserRef = useRef(authUser);
  const isFetchingStudentsRef = useRef(false);

  // Fetch students from API with pagination and search
  const fetchStudents = useCallback(async (search = '', pageNum = 1) => {
    try {
      isFetchingStudentsRef.current = true;
      // Names only: the picker and the pick read id, name and email.
      const response = await getStudentOptions(10, pageNum, search);
      const studentsList = response.data || [];
      const hasMore = studentsList.length === 10;
      return { students: studentsList, hasMore };
    } catch (err) {
      console.error('Error fetching students:', err);
      return { students: [], hasMore: false };
    } finally {
      isFetchingStudentsRef.current = false;
    }
  }, []);

  // Load students for dropdown
  const loadStudents = useCallback(
    async (search = '', pageNum = 1, append = false) => {
      setLoadingStudents(true);
      const { students: studentsList, hasMore } = await fetchStudents(search, pageNum);

      const newOptions = studentsList.map((s) => ({
        value: s.id,
        label: `${s.name} | ${s.email}`,
        student: s,
      }));

      if (append) {
        setStudentOptions((prev) => [...prev, ...newOptions]);
      } else {
        setStudentOptions(newOptions);
      }

      setHasMoreStudents(hasMore);
      setLoadingStudents(false);

      return { students: studentsList, hasMore };
    },
    [fetchStudents],
  );

  // Load more students for infinite scroll
  const handleLoadMore = useCallback(async () => {
    if (!hasMoreStudents || loadingStudents || isFetchingStudentsRef.current) return;

    const nextPage = studentPage + 1;
    setStudentPage(nextPage);
    await loadStudents(studentSearchTerm, nextPage, true);
  }, [hasMoreStudents, loadingStudents, studentPage, studentSearchTerm, loadStudents]);

  // Debounced search handler for the staff dropdown
  const handleStudentSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setStudentSearchTerm(inputValue);
        setStudentPage(1);
        await loadStudents(inputValue, 1, false);
      }, 700),
    [loadStudents],
  );

  // Handle dropdown open - load students only when dropdown is opened
  const handleDropdownOpen = useCallback(async () => {
    if (isStaffRef.current && studentOptions.length === 0 && !loadingStudents) {
      await loadStudents('', 1, false);
    }
  }, [studentOptions.length, loadingStudents, loadStudents]);

  const handleRefreshStudents = useCallback(async () => {
    setStudentSearchTerm('');
    setStudentPage(1);
    await loadStudents('', 1, false);
  }, [loadStudents]);

  const fetchDashboardData = useCallback(
    async (fetchParams = {}) => {
      const studentId = fetchParams.studentId || null;
      const email = fetchParams.email || null;

      // Use auth user as fallback for tracking self-fetches
      const effectiveId =
        studentId || (email ? null : authUserRef.current?.id || authUserRef.current?._id);
      const effectiveEmail = email || (studentId ? null : authUserRef.current?.email);

      // Prevent redundant or concurrent fetches
      const isFetchingSame =
        isFetchingRef.current &&
        lastFetchedIdRef.current === effectiveId &&
        lastFetchedEmailRef.current === effectiveEmail;

      // If we've already successfully fetched this exact student, don't fetch
      // again unless forced
      const isAlreadyLoaded =
        dashboardData &&
        lastFetchedIdRef.current === effectiveId &&
        lastFetchedEmailRef.current === effectiveEmail;

      if ((isFetchingSame || (isAlreadyLoaded && effectiveId)) && !fetchParams.force) return;

      /* Four requests, fired together, one per section: the batches (and
         who the student is), attendance, courses and suggested batches.
         Each lands into the page on its own -- nothing waits for the
         slowest. The skeleton is up only until the batches arrive, since
         they are the bulk of the page; the other sections fill in as their
         answers come. A newer load (staff picking another student) makes
         every answer still in flight from this one land nowhere. */
      const seq = ++loadSeqRef.current;
      const isCurrent = () => seq === loadSeqRef.current;

      // Keep the global "Working" overlay off it, or the skeleton is never seen.
      silenceFor();
      setLoading(true);
      setReady(NOT_READY);
      // Each section's request marks it answered, success or not -- a
      // failed section shows its empty state rather than shimmering on.
      const answered = (key) => () => {
        if (isCurrent()) setReady((r) => ({ ...r, [key]: true }));
      };
      // A new subject starts from nothing -- never one student's courses
      // beside another's batches while the answers arrive.
      setDashboardData(null);
      setEnrolledCourses([]);
      isFetchingRef.current = true;
      lastFetchedIdRef.current = effectiveId;
      lastFetchedEmailRef.current = effectiveEmail;

      const params = {};
      if (studentId) params.studentId = studentId;
      if (email) params.email = email;

      const viewer = authUserRef.current;
      const staffStatus = isStaffViewer(viewer);
      if (viewer) {
        setIsStaff(staffStatus);
        isStaffRef.current = staffStatus;
      }

      const merge = (part) => {
        if (!isCurrent() || !part) return;
        setDashboardData((prev) => ({ ...(prev || {}), ...part }));
      };

      const batchesReq = getStudentDashboardBatches(params)
        .then((res) => {
          if (!isCurrent()) return;
          const part = res?.data || {};
          merge(part);

          const student = part.student || null;
          if (student?.id) {
            setSelectedStudentId(String(student.id));
            setSelectedStudentName(student.name || '');
            setSelectedStudentEmail(student.email || '');
            setStudentName(student.name || viewer?.name || 'Student');
            setSelectedStudentValue({
              value: student.id,
              label: `${student.name} | ${student.email}`,
              student,
            });
          }
        })
        .catch((error) => console.error('Error fetching student batches:', error))
        .finally(() => {
          if (isCurrent()) setLoading(false);
          answered('batches')();
        });

      const attendanceReq = getStudentDashboardAttendance(params)
        .then((res) => merge(res?.data))
        .catch((error) => console.error('Error fetching student attendance:', error))
        .finally(answered('attendance'));

      // Course rows arrive ready to print: name, trainer and status are
      // all worked out by the API.
      const coursesReq = getStudentDashboardCourses(params)
        .then((res) => {
          if (!isCurrent()) return;
          const part = res?.data || {};
          merge({ totalCourseEnrolled: part.totalCourseEnrolled ?? 0 });
          setEnrolledCourses(Array.isArray(part.enrolledCourses) ? part.enrolledCourses : []);
        })
        .catch((error) => console.error('Error fetching student courses:', error))
        .finally(answered('courses'));

      const suggestedReq = getStudentDashboardSuggested(params)
        .then((res) => merge(res?.data))
        .catch((error) => console.error('Error fetching suggested batches:', error))
        .finally(answered('suggested'));

      await Promise.allSettled([batchesReq, attendanceReq, coursesReq, suggestedReq]);
      if (isCurrent()) isFetchingRef.current = false;
    },
    [dashboardData],
  );

  useEffect(() => {
    if (authUser) {
      authUserRef.current = authUser;
      const status = isStaffViewer(authUser);
      setIsStaff(status);
      isStaffRef.current = status;

      // Parse email from URL on mount only
      const searchParams = new URLSearchParams(location.search);
      const emailParam = searchParams.get('email');

      if (status) {
        // Staff: only fetch if an email is provided in the URL, otherwise
        // wait at the gate for a student to be picked.
        if (emailParam && !lastFetchedEmailRef.current) {
          setSelectedStudentEmail(emailParam);
          fetchDashboardData({ email: emailParam });
        } else {
          setLoading(false);
        }
      } else if (!lastFetchedIdRef.current && !isFetchingRef.current) {
        // A student always fetches their own dashboard.
        fetchDashboardData();
      }
    }
  }, [authUser, fetchDashboardData, location.search]);

  const handleStudentSelection = useCallback(
    async (selectedOptions) => {
      const selectedOption =
        selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;

      if (!selectedOption) {
        // Answers still in flight for the last student land nowhere.
        loadSeqRef.current += 1;
        setSelectedStudentId(null);
        setSelectedStudentName('');
        setSelectedStudentEmail('');
        setSelectedStudentValue(null);
        setDashboardData(null);
        setEnrolledCourses([]);
        return;
      }

      const { value: studentId, student } = selectedOption;
      setSelectedStudentId(studentId);
      setSelectedStudentName(student?.name || '');
      setSelectedStudentEmail(student?.email || '');
      setSelectedStudentValue(selectedOption);

      // Update data without changing the URL
      if (student?.email) {
        await fetchDashboardData({ email: student.email });
      } else {
        await fetchDashboardData({ studentId });
      }
    },
    [fetchDashboardData],
  );

  const handleClearSelection = () => {
    // Answers still in flight for the last student land nowhere.
    loadSeqRef.current += 1;
    isFetchingRef.current = false;
    setSelectedStudentId(null);
    setSelectedStudentName('');
    setSelectedStudentEmail('');
    setSelectedStudentValue(null);
    setDashboardData(null);
    setEnrolledCourses([]);
    lastFetchedIdRef.current = null;
    lastFetchedEmailRef.current = null;
  };

  /* One dropdown, rendered into the gate before a student is picked and
     into the picker strip afterwards. */
  const studentPicker = (
    <CustomAsyncDropdown
      inputId="sv-student-picker"
      options={studentOptions}
      value={selectedStudentValue ? [selectedStudentValue] : []}
      onChange={handleStudentSelection}
      placeholder="Search by name, email or phone..."
      isMulti={false}
      isSearchable={true}
      isLoading={loadingStudents}
      onInputChange={handleStudentSearch}
      onMenuScrollToBottom={handleLoadMore}
      onMenuOpen={handleDropdownOpen}
      onRefresh={handleRefreshStudents}
      isDisabled={false}
      loadingMessage="Loading students..."
      noOptionsMessage="No students found"
      noSearchResultsMessage="No students matched your search"
    />
  );

  /* Staff with nobody selected. The dashboard has no subject yet, so
     there is nothing to draw but the picker. */
  if (isStaff && !selectedStudentId && !selectedStudentEmail) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sv-gate">
            <span className="sv-gate-mark">
              <UserCheck size={22} aria-hidden="true" />
            </span>
            <h1 className="sv-gate-title">Pick a student</h1>
            <p className="sv-gate-text">
              Choose a student to read their batches, courses and attendance.
            </p>

            <div className="sv-gate-field">{studentPicker}</div>

            <span className="sv-gate-tip">
              <AlertCircle size={14} aria-hidden="true" />
              Search by name, email or phone number.
            </span>
          </div>
        </div>
      </div>
    );
  }

  const displayName = isStaff ? selectedStudentName : studentName;
  const displayEmail = isStaff ? selectedStudentEmail : authUser?.email || selectedStudentEmail;

  if (isStaff) {
    return (
      <StaffStudentView
        loading={loading}
        dashboardData={dashboardData}
        enrolledCourses={enrolledCourses}
        studentName={displayName}
        studentEmail={displayEmail}
        studentId={selectedStudentId}
        picker={studentPicker}
        onClear={handleClearSelection}
        requestsRefreshSignal={requestsRefreshSignal}
      />
    );
  }

  return (
    <StudentFeedView
      loading={loading}
      ready={ready}
      dashboardData={dashboardData}
      enrolledCourses={enrolledCourses}
      studentName={displayName}
      studentEmail={displayEmail}
      studentId={selectedStudentId}
      requestsRefreshSignal={requestsRefreshSignal}
    />
  );
}

export default StudentDashboard2;
